import React, { FC, memo, useCallback, useContext } from "react";
import { useMedia } from "react-use";

import { IHttpRequestConfig } from "@/browser";
import { IFetcherRenderProps } from "@/components/Fetcher";
import { FullScreenRow } from "@/components/Grid";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { fetchJobRequest, IJobRequest } from "@/data/job-requests";
import { useFetcher } from "@/hooks/http";
import { withRouteProps } from "@/infrastructure/Route";
import { Container } from "@/views/components/Container";
import { JobRequestHeader } from "@/views/components/JobRequests/Partials/JobRequestIconHeader/JobRequestHeader";
import { withHttpError } from "@/views/Errors";

import { transformRouteProps } from "../selectors";
import { IJobRequestDetailRouteProps } from "../types";
import { JobRequestDetailRendererComponent } from "./JobRequestDetail";

interface IJobRequestDetailRendererProps extends IFetcherRenderProps<IJobRequest> {
  onApplyModalClose?: () => void;
}

const JobRequestDetailWithApplicationRendererComponent: FC<IJobRequestDetailRendererProps> = memo(props => {
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const baseClass = "job-request-with-application";

  return (
    <>
      <JobRequestHeader onApplyModalClose={props.onApplyModalClose} {...props} />
      <Container className={baseClass} padding={isSmallScreen ? ["md", false] : undefined}>
        <FullScreenRow>
          <JobRequestDetailRendererComponent {...props} />
        </FullScreenRow>
      </Container>
    </>
  );
});

const JobRequestDetailWithApplicationRenderer = withHttpError(JobRequestDetailWithApplicationRendererComponent);

interface IJobRequestFetcherProps extends IJobRequestDetailRouteProps {}

const JobRequestFetcherComponent: React.FunctionComponent<IJobRequestFetcherProps> = React.memo(({ routeProps }) => {
  const { contextCompany } = useContext(UserContext);

  const handleFetchData = (_: any, httpConfig: IHttpRequestConfig) => {
    const { jobRequestId } = routeProps;
    if (contextCompany) {
      return fetchJobRequest({ jobRequestId, companyId: contextCompany.id }, httpConfig);
    }
    return fetchJobRequest({ jobRequestId }, httpConfig);
  };

  const fetcher = useFetcher(handleFetchData, {}, [routeProps.jobRequestId]);

  const handleApplyModalClosed = useCallback(() => {
    fetcher.fetch();
  }, [fetcher]);

  return <JobRequestDetailWithApplicationRenderer {...fetcher} onApplyModalClose={handleApplyModalClosed} />;
});

export const JobRequestDetailWithApplications = withRouteProps(JobRequestFetcherComponent, transformRouteProps);
